import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import api from '../../api';
import {
    LOGISTICS_STATUSES,
    PAYOUT_STATUSES,
    PRODUCTION_STATUSES,
    canCompleteOrder,
    isOrderCompleted,
    logisticsLabel,
    payoutLabel,
    productionLabel,
} from '../../helpers/orderStages';

const PRODUCTION_OPTIONS = PRODUCTION_STATUSES.filter((status) => status !== 'not_applicable');
const LOGISTICS_OPTIONS = LOGISTICS_STATUSES.filter((status) => status !== 'not_applicable' && status !== 'blocked');
const PAYOUT_OPTIONS = PAYOUT_STATUSES.filter((status) => status !== 'not_applicable');

export default function OrderStagesPanel({ order, onOrderUpdated }) {
    const [saving, setSaving] = useState('');
    const [error, setError] = useState('');

    if (!order?.id) {
        return null;
    }

    const currentByLane = {
        production: order.production_status,
        logistics: order.logistics_status,
        payout: order.payout_status,
    };

    const changeStage = async (lane, status) => {
        if (!status || status === currentByLane[lane]) {
            return;
        }
        setSaving(lane);
        setError('');
        try {
            const response = await api.patch(`orders/${order.id}/stages`, { lane, status });
            if (response.data?.data && typeof onOrderUpdated === 'function') {
                onOrderUpdated(response.data.data);
            }
        } catch (err) {
            setError(
                err?.response?.data?.message
                || err?.response?.data?.errors?.status
                || 'Não foi possível atualizar a etapa.',
            );
        } finally {
            setSaving('');
        }
    };

    const completed = isOrderCompleted(order);
    const productionLocked = order.production_status === 'not_applicable';
    const logisticsLocked = order.logistics_status === 'not_applicable';
    const payoutLocked = order.payout_status === 'not_applicable';
    const showComplete = canCompleteOrder(order);
    const needsPayoutRelease = completed && order.payout_status === 'held' && !payoutLocked;

    const completeOrder = async () => {
        if (!showComplete && !needsPayoutRelease) {
            return;
        }
        setSaving('completion');
        setError('');
        try {
            const response = await api.patch(`orders/${order.id}/stages`, { lane: 'completion', status: 'completed' });
            if (response.data?.data && typeof onOrderUpdated === 'function') {
                onOrderUpdated(response.data.data);
            }
        } catch (err) {
            setError(
                err?.response?.data?.message
                || err?.response?.data?.errors?.status
                || 'Não foi possível concluir o pedido.',
            );
        } finally {
            setSaving('');
        }
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Etapas do pedido
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {completed
                    ? 'Pedido concluído. Ele permanece na lista e sai do quadro de pedidos.'
                    : 'Altere o status de cada faixa. No quadro de pedidos, arraste o card para a coluna desejada. Conclua somente após entregue, não entregue ou devolvido.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
                <TextField
                    select
                    size="small"
                    label="Produção"
                    value={productionLocked ? 'not_applicable' : order.production_status || ''}
                    disabled={completed || productionLocked || saving === 'production'}
                    onChange={(e) => changeStage('production', e.target.value)}
                    sx={{ minWidth: 200, flex: '1 1 200px' }}
                    helperText={productionLocked ? 'Não se aplica a este pedido.' : (completed ? 'Pedido concluído.' : ' ')}
                >
                    {productionLocked ? (
                        <MenuItem value="not_applicable">{productionLabel('not_applicable')}</MenuItem>
                    ) : (
                        PRODUCTION_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>{productionLabel(status)}</MenuItem>
                        ))
                    )}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Logística"
                    value={logisticsLocked ? 'not_applicable' : (order.logistics_status === 'blocked' ? 'blocked' : order.logistics_status || '')}
                    disabled={completed || logisticsLocked || saving === 'logistics'}
                    onChange={(e) => changeStage('logistics', e.target.value)}
                    sx={{ minWidth: 220, flex: '1 1 220px' }}
                    helperText={logisticsLocked ? 'Não se aplica a este pedido.' : (completed ? 'Pedido concluído.' : ' ')}
                >
                    {logisticsLocked ? (
                        <MenuItem value="not_applicable">{logisticsLabel('not_applicable')}</MenuItem>
                    ) : (
                        [
                            ...(order.logistics_status === 'blocked' ? ['blocked'] : []),
                            ...LOGISTICS_OPTIONS,
                        ].map((status) => (
                            <MenuItem key={status} value={status}>{logisticsLabel(status)}</MenuItem>
                        ))
                    )}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Repasse"
                    value={payoutLocked ? 'not_applicable' : order.payout_status || ''}
                    disabled={(completed && !needsPayoutRelease) || payoutLocked || saving === 'payout'}
                    onChange={(e) => changeStage('payout', e.target.value)}
                    sx={{ minWidth: 180, flex: '1 1 180px' }}
                    helperText={payoutLocked ? 'Não se aplica a este pedido.' : (needsPayoutRelease ? 'Pedido concluído: libere o repasse.' : (completed ? 'Pedido concluído.' : ' '))}
                >
                    {payoutLocked ? (
                        <MenuItem value="not_applicable">{payoutLabel('not_applicable')}</MenuItem>
                    ) : (
                        PAYOUT_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>{payoutLabel(status)}</MenuItem>
                        ))
                    )}
                </TextField>
            </Stack>
            {showComplete ? (
                <Button
                    variant="contained"
                    color="success"
                    sx={{ mt: 1 }}
                    disabled={saving === 'completion'}
                    onClick={completeOrder}
                >
                    Concluir pedido
                </Button>
            ) : null}
            {needsPayoutRelease ? (
                <Button
                    variant="contained"
                    color="success"
                    sx={{ mt: 1 }}
                    disabled={saving === 'completion'}
                    onClick={completeOrder}
                >
                    Liberar repasse
                </Button>
            ) : null}
            {error ? (
                <Typography color="error" variant="body2" sx={{ mt: 0.5 }}>
                    {error}
                </Typography>
            ) : null}
        </Box>
    );
}
